import {ActionIcon, Flex, Title} from "@mantine/core";
import {Select, Space} from "@mantine/core";
import {Stack, Switch, Text} from "@mantine/core";
import {TextInput} from "@mantine/core";
import {ReactNode, useContext, useMemo, useState} from "react";
import {Row, Col} from "../../components/Flex/Flex";
import Icon from "../../components/Icon/Icon.tsx";
import IconMap from "../../components/Icon/IconMap.ts";
import {calculateTime, calculateTimeSplit} from "../../utils/hyper.ts";
import {generateSystemPlanRelative} from "../../utils/TravelPlanner.ts";
import BonusChip from "./BonusChip.tsx";
import RouteMap from "./RouteMap.tsx";
import NumberSelector from "./NumberSelector.tsx";
import { ArrowClockwise as IconRefresh, Pencil as IconPencil, FloppyDisk as IconDeviceFloppy, MapPin as IconMapPin } from "@phosphor-icons/react";
import api, {useSWCLocation, useSystems} from "../../utils/api";
import MessageContext from "../../context/MessageContext.tsx";
import mapStyle from '../../style/Map.module.css';
import style from './RouteDetails.module.css';
import {RouteGroup, Segment} from "./types.ts";
import {VersionBadge} from "./VersionBadge.tsx";


const noDirectTime = <i>Not available, recalculate this route</i>;


const flipTimes = (data: Segment[]) => {
  return data.map((el, index, array) => {
    const r = {...el};
    if ((index + 1) === array.length) {
      r.cost = 0;
      r.baseCost = 0;
    } else {
      const n = array[index + 1];
      r.cost = n.cost;
      r.baseCost = n.baseCost;
    }
    return r;
  });
};

interface RouteDetailsProps {
  route: RouteGroup;
  skill: number | undefined,
  hyper: number | undefined,
  sublight: number | undefined,
  onSkillChange: (value: number | undefined) => void,
  onHyperChange: (value: number | undefined) => void,
  onSublightChange: (value: number | undefined) => void,
  onRouteUpdate: (data: any) => void,
  presetControl?: ReactNode,
}

interface RouteSelectOption {
  value: string;
  label: string;
}

export default function RouteDetails({
  route,
  skill,
  hyper,
  sublight,
  onSkillChange,
  onHyperChange,
  onSublightChange,
  onRouteUpdate,
  presetControl,
}: RouteDetailsProps) {
  const [selected, setSelected] = useState<number>(0);
  const [highlight, setHighlight] = useState<string>('');
  const [flipped, setFlipped] = useState<boolean>(false);
  const {location} = useSWCLocation();
  const {systems} = useSystems();
  const {setLoaderMessage, success} = useContext(MessageContext);


  const actualRoute = useMemo(() => {
    if (flipped) {
      const r = {...route.routes[selected]};
      r.path = flipTimes(r.path).reverse();
      return r;
    }
    return route.routes[selected];
  }, [route.routes, selected, flipped]);


  const routeTotals = useMemo(() => {
    return actualRoute.path.reduce((acc, segment, index) => {
      if (index === 0) {
        return acc;
      }
      const hyperBase = segment.hyperCost ?? segment.cost ?? 0;
      const subBase = segment.sublightCost ?? 0;
      return {
        hyperBase: acc.hyperBase + hyperBase,
        sublightBase: acc.sublightBase + subBase,
        totalBase: acc.totalBase + (segment.cost ?? 0),
      };
    }, {hyperBase: 0, sublightBase: 0, totalBase: 0});
  }, [actualRoute.path]);

  const isV2 = (route.version ?? 1) >= 2;

  const reducedTime = useMemo(() => {
    const applyHyper = hyper || 1;
    const applySublight = sublight || 1;
    const applySkill = skill || 0;
    if (isV2) {
      return calculateTimeSplit(routeTotals.hyperBase, routeTotals.sublightBase, applyHyper, applySublight, applySkill);
    }
    return calculateTime(actualRoute.cost, applyHyper, applySkill);
  }, [routeTotals, hyper, sublight, skill, isV2, actualRoute.cost]);

  const fullTime = useMemo(() => {
    const applyHyper = hyper || 1;
    const applySublight = sublight || 1;
    const applySkill = skill || 0;
    if (isV2) {
      return calculateTimeSplit(route.baseCost ?? 0, 0, applyHyper, applySublight, applySkill);
    }
    return calculateTime(route.baseCost ?? 0, applyHyper, applySkill);
  }, [route, skill, hyper, sublight, isV2]);

  const diffPerc: number = isV2
    ? Math.round((routeTotals.totalBase - route.baseCost) * 100 / route.baseCost)
    : Math.round((actualRoute.cost - route.baseCost) * 100 / route.baseCost);


  let ignore = null;
  if (route.exclude.length > 0) {
    let array = [];
    for (let id of route.exclude) {
      let s = systems.find(s => s.id === id);
      if (s) {
        array.push(s.name);
      }
    }
    if (array.length > 0) {
      ignore = <div style={{marginTop: '0.25rem', marginBottom: '0.25rem'}}>(Skipping {array.join(', ')})</div>;
    }
  }


  const routeOptions: RouteSelectOption[] = useMemo(() => {
    return route.routes.map((r, index) => {
      if (!isV2) {
        return {
          value: String(index),
          label: `${r.path.length - 1} jumps (${((r.cost - route.baseCost) * 100 / route.baseCost).toFixed(0)}%)`
        };
      }
      const totals = r.path.reduce((acc, segment, idx) => {
        if (idx === 0) {
          return acc;
        }
        const hyperBase = segment.hyperCost ?? segment.cost ?? 0;
        const subBase = segment.sublightCost ?? 0;
        return {
          hyperBase: acc.hyperBase + hyperBase,
          sublightBase: acc.sublightBase + subBase,
          totalBase: acc.totalBase + (segment.cost ?? 0),
        };
      }, {hyperBase: 0, sublightBase: 0, totalBase: 0});
      return {
        value: String(index),
        label: `${r.path.length - 1} jumps (${((totals.totalBase - route.baseCost) * 100 / route.baseCost).toFixed(0)}%)`
      };
    });
  }, [route.routes, route.baseCost, isV2]);

  const applyHyper = hyper || 1;
  const applySublight = sublight || 1;
  const applySkill = skill || 0;

  return (
    <Stack gap="sm">
      <DetailHeader route={route} onNameChange={onRouteUpdate}/>
      <Row align="center">
        {presetControl ? <Col style={{minWidth: '200px'}}>{presetControl}</Col> : null}
        <Col>
          <NumberSelector min={0} max={5} value={skill} onChange={val => onSkillChange(val)} label="Pilot Skill"/>
        </Col>
        <Col>
          <NumberSelector min={1} max={15} value={hyper} onChange={val => onHyperChange(val)} label="Hyper speed"/>
        </Col>
        <Col style={{minWidth: '120px'}}>
          <NumberSelector min={0} max={300} step={5} value={sublight} onChange={val => onSublightChange(val)} label="Sublight speed"/>
        </Col>
        <Col style={{lineHeight: 1.75}}>
          <b>Total time: </b>{reducedTime.toHuman()}<br/>
          <b>Direct route: </b>{route.baseCost ? fullTime.toHuman() : noDirectTime}<br/>
          <b>Time saved: </b>{route.baseCost ? (fullTime.minus(reducedTime).rescale()).toHuman() : noDirectTime}
        </Col>
        <Col>
          {route.baseCost ? <BonusChip value={diffPerc}/> : null}
        </Col>
        <Col>
          <Select data={routeOptions} value={String(selected)} onChange={(val) => setSelected(Number(val))}/>
        </Col>
        {!isV2 ? (
          <Col>
            <Switch id="flip" style={{marginRight: '0.5rem'}} checked={flipped} onChange={(e) => setFlipped(e.currentTarget.checked)}/>
            <label htmlFor="flip">View return</label>
          </Col>
        ) : null}
        <Col grow={1}/>
        <Col>
          <VersionBadge version={route.version} />
        </Col>
        <Col>
          <ActionIcon variant="filled" style={{borderRadius: '50%'}} onClick={() => {
            setLoaderMessage('Recalculating hyper route');
            api.post('/hyper/v2/route/' + route.id + '/refresh').then(res => {
              onRouteUpdate(res.data);
              success('Refresh successful', 'The hyper route has been recalculated')
            }).finally(() => setLoaderMessage(''));
          }} title="Recalculate route"><IconRefresh size={16}/></ActionIcon>
        </Col>
      </Row>
      {ignore}
      <Space h="sm" />
      <Flex direction={{base: 'column', md: 'row'}} align="stretch" justify="center" gap="md">
      {/*<Row style={{marginTop: '0.25rem', flexDirection: 'column'}} className="md:flex-row" align="stretch" justify="center">*/}
        <RouteMap route={actualRoute} otherRoutes={route.routes} start={route.start} end={route.end} highlight={highlight} flipped={flipped}/>
        <Col grow={1} shrink={0} basis="260px">
          {actualRoute.path.map((stop, index, list) => (
            <div key={`${stop.id}-${index}`}>
              <StopSystem
                stop={stop}
                currentSystemId={location?.system?.attributes?.uid ?? null}
                prev={index > 0 ? list[index - 1] : null}
                onHover={setHighlight}
              />
              {index + 1 < list.length ? (
                <StopLeg
                  from={stop}
                  to={list[index + 1]}
                  hyper={applyHyper}
                  sublight={applySublight}
                  skill={applySkill}
                  isV2={isV2}
                />
              ) : null}
            </div>
          ))}
          {isV2 && routeTotals.sublightBase > 0 ? (
            <div style={{lineHeight: 1.75, marginTop: '3em'}}>
              <b>Hyper time: </b>{calculateTimeSplit(routeTotals.hyperBase, 0, applyHyper, applySublight, applySkill).toHuman()}<br/>
              <b>Sublight time: </b>{calculateTimeSplit(0, routeTotals.sublightBase, applyHyper, applySublight, applySkill).toHuman()}
            </div>
          ) : null}
        </Col>
      </Flex>
    </Stack>
  );
}


interface DetailHeaderProps {
  route: RouteGroup,
  onNameChange: (name: string) => void,
}

const DetailHeader = ({route, onNameChange}: DetailHeaderProps) => {
  const [name, setName] = useState<string>(route.name);
  const [edit, setEdit] = useState<boolean>(false);
  const {success, error} = useContext(MessageContext);

  let n;
  if (edit) {
    n = <TextInput size="md" value={name} onChange={e => setName(e.target.value)}/>;
  } else {
    n = <Title order={2} style={{margin: 0}}>{route.name}</Title>;
  }

  const save = () => {
    api.post('/hyper/v2/route/' + route.id + '/name', {name}).then((res) => {
      success("Route name has been updated", "");
      onNameChange(res.data);
      setEdit(false);
    })
      // @ts-ignore
      .catch((err) => {
        error("An error occurred", err?.response?.data?.message || 'Unknown error');
      })
  }

  return <Flex align="center">
    <IconPencil size={16} style={{marginRight: '0.5rem', cursor: 'pointer'}} onClick={() => setEdit(p => !p)}/>
    {n}
    {edit ? <IconDeviceFloppy size={16} style={{marginLeft: '0.5rem', cursor: 'pointer'}} onClick={save}/> : null}
  </Flex>
}


interface StopSystemProps {
  stop: Segment,
  currentSystemId: string | null,
  prev: Segment | null,
  onHover: (a: string) => void
}

const StopSystem = ({stop, currentSystemId, prev, onHover}: StopSystemProps) => {
  const here = currentSystemId === stop.id
    ? <span className={mapStyle.textPurple} title="You are here!" style={{marginLeft: '0.25rem'}}><IconMapPin size={14}/></span>
    : null;

  return (
    <Row align="center" mb={1} gap={1} wrap={false}>
      <Col basis={20} shrink={0}>
        <span className={style.stopPlannerCell}>
          {prev ? (
            <a
              href={generateSystemPlanRelative(stop.x, stop.y, prev.x, prev.y)}
              target="_blank"
              className={`travel-planner ${style.stopPlannerLink}`}
              title="Travel Planner"
            >
              <Icon icon={IconMap.TravelPlanner}/>
            </a>
          ) : (
            <span className={style.stopPlannerSpacer}/>
          )}
        </span>
      </Col>
      <Col grow={1}>
        <Text
          span
          fw={700}
          size="xl"
          className={style.stopName}
          onMouseEnter={() => onHover(stop.id)}
          onMouseLeave={() => onHover('')}
        >
          {stop.name ?? 'Deep Space'}
        </Text>
        {here}
      </Col>
      <Col shrink={0}>
        <Text size="sm" c="dimmed" className={style.monoNums}>({stop.x}, {stop.y})</Text>
      </Col>
    </Row>
  )
}


interface StopLegProps {
  from: Segment,
  to: Segment,
  hyper: number,
  sublight: number,
  skill: number,
  isV2: boolean,
}

// Sublight is bound to `from` (the previous stop): physically it happens at that
// system after arriving there, even though the backend bookkeeps it on the segment
// representing the destination of the inbound hyper. We render it on the outbound
// leg so it sits visually after the system where the maneuver occurs.
const StopLeg = ({from, to, hyper, sublight, skill, isV2}: StopLegProps) => {
  let time;
  if (isV2 && (to.hyperCost !== undefined || to.sublightCost !== undefined)) {
    time = calculateTimeSplit(to.hyperCost ?? to.cost ?? 0, 0, hyper, sublight, skill);
  } else {
    time = calculateTime(to.cost, hyper, skill);
  }
  const showSublight = isV2 && (from.sublightCost ?? 0) > 0;
  const sublightTime = showSublight
    ? calculateTimeSplit(0, from.sublightCost ?? 0, hyper, sublight, skill)
    : null;
  const bonusBase = to.hyperBaseCost ?? to.baseCost;
  const cost = to.hyperCost ?? to.cost;
  const bonus = bonusBase > 0 ? Math.round((cost - bonusBase) * 100 / bonusBase) : 0;
  const color = bonus > -15 ? 'bonus-inline-good' : 'bonus-inline-great';
  const hasLegTime = time.as('minutes') > 0;

  return (
    <div className={style.legCard}>
      {showSublight ? (
        <Row align="center" className={style.sublightRow}>
          <Col grow={1}>
            <Text size="xs" c="dimmed">
              Sublight {from.sublightTarget ? `to (${from.sublightTarget.x}, ${from.sublightTarget.y})` : ''}
            </Text>
          </Col>
          <Col style={{textAlign: 'right'}}>
            <Text size="xs" className={style.monoNums}>
              {sublightTime?.toHuman({unitDisplay: 'short'})}
            </Text>
          </Col>
        </Row>
      ) : null}
      <Row align="center" wrap={false}>
        <Col grow={1}>
          <Text size="xs" c="dimmed">
            Hyper
          </Text>
        </Col>
        <Col style={{textAlign: 'right'}}>
          <Text span size="sm" fw={600} className={style.monoNums}>
            {hasLegTime ? time.toHuman({unitDisplay: 'short'}) : 'Instant'}
          </Text>
          {hasLegTime ? <small className={`${color} ${style.legBonus}`}>{bonus}%</small> : null}
        </Col>
      </Row>
    </div>
  )
}
